import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isGuidesOpen, setIsGuidesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<'categories' | 'guides'>('categories');
  const categoryRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setIsGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinkClass = (isDark: boolean) =>
    `text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
      isDark ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/90 hover:text-white'
    }`;

  const currentLinkClass = isScrolled ? navLinkClass(true) : navLinkClass(false);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 navbar-blur border-b border-background-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-[72px]">
        <Link
          to="/"
          className={`font-heading text-xl md:text-2xl font-semibold whitespace-nowrap transition-colors duration-300 ${
            isScrolled ? 'text-foreground-950' : 'text-white'
          }`}
        >
          Hipobuy Sheet Hub
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className={currentLinkClass}>
            Home
          </Link>

          <div ref={categoryRef} className="relative">
            <button
              onClick={() => { setIsCategoryOpen(!isCategoryOpen); setIsGuidesOpen(false); }}
              className={`flex items-center gap-1.5 ${currentLinkClass}`}
            >
              Categories
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-base ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>

            {isCategoryOpen && (
              <div className="absolute top-full mt-3 left-0 bg-background-50 rounded-xl border border-background-200/70 py-2 w-64 z-50"
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
              >
                <div className="px-4 py-1.5">
                  <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest">Product Categories</p>
                </div>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => setIsCategoryOpen(false)}
                  >
                    <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm text-accent-600`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div ref={guidesRef} className="relative">
            <button
              onClick={() => { setIsGuidesOpen(!isGuidesOpen); setIsCategoryOpen(false); }}
              className={`flex items-center gap-1.5 ${currentLinkClass}`}
            >
              Guides
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-base ${isGuidesOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>

            {isGuidesOpen && (
              <div className="absolute top-full mt-3 left-0 bg-background-50 rounded-xl border border-background-200/70 py-2 w-72 z-50"
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
              >
                <div className="px-4 py-1.5">
                  <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest">Shopping Guides</p>
                </div>
                {[
                  { name: 'Complete Guide 2026', path: '/hipobuy-spreadsheet-guide', icon: 'ri-book-open-line', desc: 'Everything about hipobuy spreadsheet' },
                  { name: 'Platform Comparison', path: '/hipobuy-spreadsheet-vs-kakobuy', icon: 'ri-scales-3-line', desc: 'Hipobuy vs Kakobuy analysis' },
                  { name: 'Safety & Security Guide', path: '/hipobuy-spreadsheet-safety', icon: 'ri-shield-check-line', desc: 'Stay safe while shopping' },
                ].map((guide) => (
                  <Link
                    key={guide.path}
                    to={guide.path}
                    className="flex items-start gap-3 px-4 py-3 text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setIsGuidesOpen(false)}
                  >
                    <span className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${guide.icon} text-sm text-secondary-600`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{guide.name}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{guide.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
              isScrolled
                ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
            }`}
          >
            hipobuy sheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              isScrolled ? 'text-foreground-950' : 'text-white'
            }`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/50 animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-3">
            <Link
              to="/"
              className="flex items-center gap-3 py-3.5 px-4 rounded-xl bg-background-100 mb-3 text-sm font-semibold text-foreground-900 hover:bg-background-200/50 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-home-4-line text-base text-primary-600"></i>
              </span>
              Home
            </Link>

            <div className="flex rounded-full bg-background-100 p-1 mb-3">
              <button
                onClick={() => setMobileActiveTab('categories')}
                className={`flex-1 py-2.5 px-4 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap ${
                  mobileActiveTab === 'categories'
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-grid-line text-xs"></i>
                </span>
                Categories
              </button>
              <button
                onClick={() => setMobileActiveTab('guides')}
                className={`flex-1 py-2.5 px-4 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap ${
                  mobileActiveTab === 'guides'
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-book-open-line text-xs"></i>
                </span>
                Guides
              </button>
            </div>

            {mobileActiveTab === 'categories' && (
              <div className="space-y-1 max-h-[50vh] overflow-y-auto pb-2">
                <div className="grid grid-cols-1 gap-1">
                  {[
                    { label: 'Apparel', cats: categories.slice(0, 8) },
                    { label: 'Accessories & Lifestyle', cats: categories.slice(8) },
                  ].map((group) => (
                    <div key={group.label} className="mb-3">
                      <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest px-2 py-1.5">{group.label}</p>
                      {group.cats.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                            <i className={`${cat.icon} text-base text-accent-600`}></i>
                          </span>
                          <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                          <span className="ml-auto w-5 h-5 flex items-center justify-center text-foreground-300">
                            <i className="ri-external-link-line text-xs"></i>
                          </span>
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mobileActiveTab === 'guides' && (
              <div className="space-y-1 pb-2">
                {[
                  { name: 'Hipobuy Spreadsheet Complete Guide 2026', path: '/hipobuy-spreadsheet-guide', icon: 'ri-book-open-line', desc: 'Everything you need to know about spreadsheet shopping' },
                  { name: 'Hipobuy vs Kakobuy: Full Comparison', path: '/hipobuy-spreadsheet-vs-kakobuy', icon: 'ri-scales-3-line', desc: 'Which spreadsheet platform is right for you' },
                  { name: 'Safety & Security Guide', path: '/hipobuy-spreadsheet-safety', icon: 'ri-shield-check-line', desc: 'Stay safe while discovering products' },
                ].map((guide) => (
                  <Link
                    key={guide.path}
                    to={guide.path}
                    className="flex items-start gap-3 py-3.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-9 h-9 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${guide.icon} text-base text-secondary-600`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{guide.name}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{guide.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="mt-4 pt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                hipobuy sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}